import { Box, Button, Container, Typography, styled } from '@mui/material';
import type React from 'react';

const StyledParagraph = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: '0.875rem',
  lineHeight: 1.6,
  [theme.breakpoints.up('sm')]: {
    fontSize: '1rem',
  },
}));

interface SplashPageProps {
  onGetStarted: () => void;
}

const SplashPage: React.FC<SplashPageProps> = ({ onGetStarted }) => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ 
        textAlign: 'center', 
        mb: 6,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        p: 4,
        backgroundColor: '#fafafa'
      }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 600,
            mb: 4,
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
          }}
        >
          AGILE Rates Centre
        </Typography>

        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 500,
            mb: 3,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            color: 'primary.main',
          }}
        >
          The AGILE Rates Centre (“the tool”) is a tool offered to support: 
        </Typography>

        <Box sx={{ textAlign: 'left', mb: 4 }}>
          <Box component="ul" sx={{ fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.6, mb: 2 }}>
            <li>Existing AGILE investors to understand what Lifetime Income Rate may be applicable to them upon commencing their Lifetime Income; or</li>
            <li>Prospective AGILE investors to understand what Lifetime Income Rate may be available to them, if they choose to make an AGILE investment within the dates specified.</li>
          </Box>
          <StyledParagraph variant="body1" sx={{fontWeight: 'bold'}}>
            The results provided are based solely on the requested information on the next screen and entered by you, including Lifetime Income Option elections indicated. The final Lifetime Income Rate applicable upon commencing the Lifetime Income is subject to our confirmation and may differ due to factors such as the investment commencement date or if any of your information or elections made are different to what has been entered in the tool. 
          </StyledParagraph>
          <StyledParagraph variant="body1" sx={{fontWeight: 'bold'}}>
            Your Lifetime Income Rate determines the percentage of Investment Value you will receive when you commence your guaranteed Lifetime Income. It comprises an Age-Based Rate with an Annual Income Escalator (which remains a constant percentage year on year) and which increases your Lifetime Income Rate for each complete year you delay commencing your Lifetime Income.  
          </StyledParagraph>
          <StyledParagraph variant="body1">
            You should ensure that you read and understand the Disclaimers and Important Information that relate to the tool, as provided in the link at the bottom of the tool. Capitalised terms where used here and in this tool have the same meaning as defined in the relevant PDS , unless the context otherwise requires. 
          </StyledParagraph>
          <StyledParagraph variant="body1">
            Whilst all efforts have been made to strive for accurate and reliable content, please note that the information provided may not be entirely error-free or up-to-date and there may be limitations or omissions present. We recommend that you independently verify the content against Lifetime Income Rates tables available on our Website at https://www.allianzretireplus.com.au/adviser-resources/agile-rates.html, or contact Allianz Retire+ on 1300 371 136 to validate any doubts or questions. We will endeavour to rectify any errors identified as soon as possible.  
          </StyledParagraph>
          <StyledParagraph variant="body1">
            If you are an existing AGILE Investor with a Commencement Date prior to 19 January 2026, you should note that while this tool will indicate the Lifetime Income Rates available to you based on the information you enter, the Lifetime Income Rates applicable to you are ultimately confirmed within your AGILE Investor Certificate.  
          </StyledParagraph>
          <StyledParagraph variant="body1">
            Personal information collected via the tool is only for the purposes of performing illustrative calculations and will be handled in accordance with the Allianz Australia Life Insurance Limited Privacy Policy available at <a href="https://www.allianzretireplus.com.au/privacy-policy.html" target="_blank" rel="noopener">https://www.allianzretireplus.com.au/privacy-policy.html</a>. 
          </StyledParagraph>
        </Box>

        <Button
          variant="contained"
          size="large"
          onClick={onGetStarted}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.125rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            mb: 3
          }}
        >
          Accept and Get Started &gt;
        </Button><br />
        <a href='https://www.allianzretireplus.com.au' target='_blank' rel="noopener">www.allianzretireplus.com.au</a>
      </Box>
    </Container>
  );
};

export default SplashPage;